import React, { type FunctionComponent, Fragment, useState } from 'react'
import { css } from '@emotion/react'
import { Button, PageHeader, spacing } from '@retailer-platform/shared-components'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { useDashMessage } from 'intl/intl.hooks'
import { BannerBreadcrumbs } from 'domains/banners/components/banner-breadcrumbs/BannerBreadcrumbs'
import { type BannerFormData, type FlyoutFormData } from 'domains/banners/types/banner.types'
import {
  useCreateFlyout,
  useCreateHeroBanner,
  useCreateSecondaryBanner,
} from 'domains/banners/api/createBanner.hooks'
import {
  isFlyout,
  isHeroBanner,
  isSecondaryBanner,
  mapFormDataToFlyoutInput,
  mapFormDataToHeroBannerInput,
  mapFormDataToSecondaryBannerInput,
} from 'domains/banners/utils/bannerUtils'
import { type PlacementTypeEnum } from 'domains/banners/api'
import { BannerForm } from 'domains/banners/components/banner-form/BannerForm'
import { FlyoutForm } from 'domains/banners/components/banner-form/FlyoutForm'
import { BannerCreateTypeSelector } from 'domains/banners/components/banner-create/components/BannerCreateTypeSelector'

const pageContainer = css({
  margin: `${spacing.X32} auto`,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
  maxWidth: '960px',
  width: '100%',
})

const confirmButtonContainer = css({
  display: 'flex',
  justifyContent: 'flex-end',
})

const confirmButtonStyles = css({
  margin: `${spacing.X24} 0 0 0`,
})

export const BannerCreate: FunctionComponent = () => {
  const continueButtonText = useDashMessage('bannersDomain.create.continueButton')

  const {
    createFlyout,
    apiLoading: apiLoadingFlyout,
    apiError: apiErrorFlyout,
    apiSuccess: apiSuccessFlyout,
  } = useCreateFlyout()

  const {
    createHeroBanner,
    apiLoading: apiLoadingHeroBanner,
    apiError: apiErrorHeroBanner,
    apiSuccess: apiSuccessHeroBanner,
  } = useCreateHeroBanner()

  const {
    createSecondaryBanner,
    apiLoading: apiLoadingSecondaryBanner,
    apiError: apiErrorSecondaryBanner,
    apiSuccess: apiSuccessSecondaryBanner,
  } = useCreateSecondaryBanner()

  const goToList = useGoToPath('app-admin-banners-list')

  if (apiSuccessFlyout || apiSuccessHeroBanner || apiSuccessSecondaryBanner) {
    goToList()
  }

  // Set the banner type when selecting a card
  const [bannerType, setBannerType] = useState<PlacementTypeEnum>(null)
  // Confirm the selection and move on to the next step
  const [selected, setSelected] = useState(false)

  const handleFlyoutFormSubmit = (formData: FlyoutFormData) => {
    createFlyout({
      bannerData: mapFormDataToFlyoutInput(formData),
    })
  }

  const handleHeroBannerFormSubmit = (formData: BannerFormData) => {
    createHeroBanner({
      bannerData: mapFormDataToHeroBannerInput(formData),
    })
  }

  const handleSecondaryBannerFormSubmit = (formData: BannerFormData) => {
    createSecondaryBanner({
      bannerData: mapFormDataToSecondaryBannerInput(formData),
    })
  }

  return (
    <Fragment>
      <PageHeader
        addBottomPadding
        title={<BannerBreadcrumbs pageType={selected ? 'create' : 'bannerTypeSelector'} />}
      />
      <div css={pageContainer}>
        {selected && isFlyout(bannerType) && (
          <FlyoutForm
            type={bannerType}
            onFormSubmit={handleFlyoutFormSubmit}
            submitError={apiErrorFlyout}
            isSubmitting={apiLoadingFlyout}
            onCancel={goToList}
          />
        )}

        {selected && isHeroBanner(bannerType) && (
          <BannerForm
            type={bannerType}
            onFormSubmit={handleHeroBannerFormSubmit}
            submitError={apiErrorHeroBanner}
            isSubmitting={apiLoadingHeroBanner}
            onCancel={goToList}
          />
        )}

        {selected && isSecondaryBanner(bannerType) && (
          <BannerForm
            type={bannerType}
            onFormSubmit={handleSecondaryBannerFormSubmit}
            submitError={apiErrorSecondaryBanner}
            isSubmitting={apiLoadingSecondaryBanner}
            onCancel={goToList}
          />
        )}
        {!selected && (
          <div data-testid="banner-select-section">
            <BannerCreateTypeSelector bannerType={bannerType} onSelectBanner={setBannerType} />
            <div css={confirmButtonContainer}>
              <Button
                data-testid="banner-create-continue-button"
                disabled={!bannerType}
                onClick={() => setSelected(true)}
                css={confirmButtonStyles}
              >
                {continueButtonText}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
}
